package com.example.mystybag.common.ui.components.custom

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.common.expected.SCImageView
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.core.extensions.ifNotEmpty
import com.example.mystybag.core.model.IconDescription
import com.example.mystybag.core.model.ImageDescription
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.views.toColor

@Composable
fun ImageDescriptionView(
    image           : ImageDescription,
    modifier        : Modifier,
    contentScale    : ContentScale  = ContentScale.Fit,
    defaultImage    : ImageResource = SRC.images.img_placeholder,
    shimmerShape    : Shape         = CircleShape,
    useOriginalSize : Boolean       = false,
    imageColor      : Color?        = null
) {
    image.link?.ifNotEmpty {
        ShimmerAsyncImage(
            modifier        = modifier,
            link            = it,
            contentScale    = contentScale,
            shimmerShape    = shimmerShape,
            errorImage      = defaultImage,
            useOriginalSize = useOriginalSize,
            colorFilter     = imageColor?.let { color -> ColorFilter.tint(color) }
        )
    } ?: image.image?.also {
        SCImageView(
            modifier        = modifier,
            image           = it,
            contentScale    = contentScale
        )
    } ?: image.icon?.also {
        IconImage(it, modifier)
    } ?: Image(
            modifier        = modifier,
            painter         = painterResource(image.resource ?: defaultImage),
            contentScale    = contentScale,
            colorFilter     = imageColor?.let { color -> ColorFilter.tint(color) }
        )
}

@Composable
fun IconImage(image: IconDescription, modifier: Modifier = Modifier) {
    val imgSizePx   = with (LocalDensity.current) { image.imageSize.toPx() }
    val radius      = remember(image.imageSize) { imgSizePx * 1.1f }
    val totalSize   = remember(image.imageSize) { image.imageSize * 2.2f }
    val background  = image.background.toColor()

    Box(
        modifier            = modifier.size(totalSize),
        contentAlignment    = Alignment.Center
    ) {
        Image(
            modifier    = Modifier
                .size(image.imageSize)
                .drawBehind {
                    drawCircle(color = background, radius = radius)
                },
            painter     = painterResource(image.resource),
            colorFilter = image.tint?.let { ColorFilter.tint(color = it.toColor()) }
        )
    }
}

@Composable
fun IconImage(resource: ImageResource, modifier: Modifier = Modifier) = IconImage(IconDescription(resource), modifier)